// ✅ src/pages/VehicleV2/TyreMaintenanceEntry.jsx
// Tyre HOME page = listing (AddTyreListing merged here)
//
// Backend mount:
//   app.use("/api/addTyreListing", addTyreListing);
//
// APIs:
//   GET  /api/addTyreListing/list
//   POST /api/addTyreListing/delete
//
// Navigation:
//   Add New  -> /vehicle/addTyreSLT
//   Edit     -> /vehicle/AddTyreEntry?tyreId=&type=
//   View     -> /vehicle/AddTyreSLT?tyreId=&type=
//
// Design template aligned with ProductionBOMListing.

import React, { useState, useContext, useEffect, useMemo } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addTyreListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const TyreMaintenanceEntry = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const loginUserId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const branchId = toInt(getLS("branchId") || getLS("branchid"), 8);

  const [tyreType, setTyreType] = useState("N");
  const [tyreSerialNo, setTyreSerialNo] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [tyreSize, setTyreSize] = useState("");
  const [vehicleRegNo, setVehicleRegNo] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const fetchList = async (pageNo = page, lim = limit) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          type: tyreType,
          branchId,
          page: pageNo,
          limit: lim,
          tyreSerialNo,
          tyreSize,
          vehicleName,
          vehicleRegNo,
          vehicleCode,
          search,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        setRows([]);
        setTotal(0);
        setPages(1);
        return;
      }

      setRows(res.data.list || []);
      setTotal(toInt(res.data.total, 0));
      setPages(toInt(res.data.pages, 1));
      setSelectedIds([]);
      setSelectAll(false);
    } catch (e) {
      toast.error("Server error while loading list");
      setRows([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [tyreType]);

  useEffect(() => {
    fetchList(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, tyreType]);

  const onSearchClick = () => {
    setPage(1);
    fetchList(1, limit);
  };

  const handleClearFilters = () => {
    setTyreSerialNo("");
    setVehicleName("");
    setTyreSize("");
    setVehicleRegNo("");
    setVehicleCode("");
    setSearch("");
    setPage(1);
    setTimeout(() => fetchList(1, limit), 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    const ids = (rows || []).map((r) => r.id).filter((x) => x > 0);
    setSelectedIds(ids);
    setSelectAll(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const ids = (rows || []).map((r) => r.id).filter((x) => x > 0);
    if (ids.length === 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(ids.every((id) => selectedIds.includes(id)));
  }, [selectedIds, rows]);

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one tyre to delete!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      const res = await axios.post(`${API_BASE}/delete`, {
        ids: selectedIds,
        type: tyreType,
        loginUserId: toInt(loginUserId),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      const failed = res.data.failedIds || [];
      const deleted = res.data.deletedIds || [];

      if (failed.length > 0 && deleted.length > 0) {
        toast.info(
          `Removed ${deleted.length}. ${failed.length} could not be deleted (already used).`
        );
      } else if (failed.length > 0) {
        toast.warn("Cannot be deleted. Tyre is already used.");
      } else {
        toast.success("Deleted successfully");
      }

      fetchList(page, limit);
    } catch (e) {
      toast.error("Server error while deleting");
    }
  };

  const handleEdit = (tyre) => {
    const id = tyre?.fld_Tyreid || tyre?.id;
    if (!id) {
      toast.error("Tyre id not found");
      return;
    }
    navigate(`/vehicle/AddTyreEntry?tyreId=${id}&type=${tyreType}`);
  };

  const handleView = (tyre) => {
    const tyreMasterId = tyre?.id;
    if (!tyreMasterId) {
      toast.error("Tyre id not found");
      return;
    }
    window.open(
      `/vehicle/AddTyreSLT?tyreId=${tyreMasterId}&type=${tyreType}`,
      "_blank"
    );
  };

  const handleAddNew = () => {
    navigate("/vehicle/addTyreSLT");
  };

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  const pagerBtn = (enabled) =>
    `px-4 py-2 rounded-lg border text-sm font-semibold ${
      enabled
        ? isDark
          ? "border-gray-600 text-gray-200 hover:bg-gray-700"
          : "border-gray-300 text-gray-700 hover:bg-white"
        : "opacity-50 cursor-not-allowed border-gray-300"
    }`;

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">TYRE MAINTENANCE</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Tyre Maintenance Listing
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={onSearchClick}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={tyreType === "N"}
                    onChange={() => setTyreType("N")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span
                    className={`text-sm font-medium ${
                      isDark ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    New Tyre
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={tyreType === "R"}
                    onChange={() => setTyreType("R")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span
                    className={`text-sm font-medium ${
                      isDark ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Retreaded Tyre
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Tyre Serial No</label>
                  <input
                    type="text"
                    value={tyreSerialNo}
                    onChange={(e) => setTyreSerialNo(e.target.value)}
                    className={inputClass}
                    placeholder="Enter serial no"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={vehicleName}
                    onChange={(e) => setVehicleName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Size</label>
                  <input
                    type="text"
                    value={tyreSize}
                    onChange={(e) => setTyreSize(e.target.value)}
                    className={inputClass}
                    placeholder="Enter tyre size"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    type="text"
                    value={vehicleRegNo}
                    onChange={(e) => setVehicleRegNo(e.target.value)}
                    className={inputClass}
                    placeholder="Enter registration no"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={vehicleCode}
                    onChange={(e) => setVehicleCode(e.target.value)}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <>
                <table
                  className={`w-full min-w-[1400px] ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Tyre Serial No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Reg No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Fitting Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Fitting Hour KM
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Removing Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Removing Hour KM
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Life
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Retread / Original
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        View
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        Edit
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={loading}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {rows.map((tyre, idx) => {
                      const checked = selectedIds.includes(tyre.id);
                      return (
                        <tr
                          key={tyre.fld_Tyreid || tyre.id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.slno ?? idx + 1 + (page - 1) * limit}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-semibold ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {tyre.tyreSerialNo || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {tyre.vehicleCode || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.vehicleRegNo || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.fittingDate || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.fittingHourKM || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.removingDate || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.removingHourKM || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-semibold ${
                              isDark ? "text-gray-200" : "text-gray-900"
                            }`}
                          >
                            {tyre.life || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {tyre.retreadOriginal || "-"}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleView(tyre)}
                              disabled={loading}
                              className="text-blue-500 hover:text-blue-600 text-lg transition"
                              title="View"
                            >
                              <FaEye />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(tyre)}
                              disabled={loading}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelect(tyre.id)}
                              disabled={loading}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <button
                    onClick={handleDelete}
                    disabled={selectedIds.length === 0 || loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      selectedIds.length === 0 || loading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    } bg-red-600 hover:bg-red-700 text-white`}
                  >
                    <FaTrash />
                    Remove
                  </button>

                  <div className="flex items-center gap-3 flex-wrap">
                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {paginationText}
                    </div>

                    <button
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={pagerBtn(page > 1 && !loading)}
                    >
                      Prev
                    </button>

                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Page {page} / {pages || 1}
                    </div>

                    <button
                      disabled={page >= pages || loading}
                      onClick={() => setPage((p) => Math.min(pages, p + 1))}
                      className={pagerBtn(page < pages && !loading)}
                    >
                      Next
                    </button>

                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(toInt(e.target.value, 10));
                        setPage(1);
                      }}
                      disabled={loading}
                      className={inputClass}
                      style={{ width: 120 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default TyreMaintenanceEntry;